// src/components/usuario/AvisosUsuario.jsx 
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId } from "../../../../utils/SessionUser";

// Helper para construir la URL completa de la imagen
const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    const baseUrl = API_BASE.replace(/\/api\/?$/, "");
    return `${baseUrl}${path}`;
};

// Placeholder image
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250' viewBox='0 0 400 250'%3E%3Crect fill='%23E5E7EB' width='400' height='250'/%3E%3Cpath fill='%239CA3AF' d='M163 142.5l24-24 24 24 48-48 48 48V187H163v-44.5zm48-39.5c-8.3 0-15-6.7-15-15s6.7-15 15-15 15 6.7 15 15-6.7 15-15 15z'/%3E%3C/svg%3E";

const mapNivelPrioridad = (tipoAviso) => {
    const nivel = String(tipoAviso || "").toLowerCase();
    if (nivel === "1" || nivel === "alta") return "alta";
    if (nivel === "2" || nivel === "media") return "media";
    return "baja";
};

const AvisosUsuario = ({ onVolver, initialItem }) => {
    const [avisos, setAvisos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estado para modal
    const [showModal, setShowModal] = useState(!!initialItem);
    const [avisoSeleccionado, setAvisoSeleccionado] = useState(initialItem || null);
    const [areasNegocio, setAreasNegocio] = useState([]);

    // Estado para filtros
    const [filtros, setFiltros] = useState({
        texto: '',
        area: 'Todas',
        nivel: 'Todas'
    });

    useEffect(() => {
        const cargarAvisos = async () => {
            const userId = getSessionUserId();
            if (!userId) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const [resAvisos, resAreas] = await Promise.all([
                    axios.get(ENDPOINTS.avisosDashboardByUsuarioId(userId)),
                    axios.get(ENDPOINTS.areasNegocios)
                ]);

                const data = resAvisos.data || [];
                const dataAreas = resAreas.data || [];

                // Crear mapa de áreas
                const areasMap = {};
                dataAreas.forEach(a => areasMap[a.idAreaNegocio || a.IdAreaNegocio] = a.nombre || a.Nombre || a.descripcion || a.Descripcion);

                setAreasNegocio(dataAreas);

                const avisosTransformados = data.map(aviso => ({
                    id: aviso.idAviso || aviso.IdAviso,
                    titulo: aviso.titulo || aviso.Titulo,
                    mensaje: aviso.mensaje || aviso.Mensaje,
                    fechaInicio: aviso.fechaInicio || aviso.FechaInicio,
                    fechaFin: aviso.fechaFin || aviso.FechaFin,
                    nivel: mapNivelPrioridad(aviso.tipoAviso || aviso.TipoAviso),
                    area: aviso.nombreArea || aviso.NombreArea || areasMap[aviso.idAreaNegocio || aviso.IdAreaNegocio] || "General",
                    activo: aviso.activo !== undefined ? aviso.activo : aviso.Activo,
                    mostrarEnDashboard: aviso.mostrarEnDashboard !== undefined ? aviso.mostrarEnDashboard : aviso.MostrarEnDashboard,
                    imagen: aviso.imagenUrl || aviso.ImagenUrl
                }));

                // Ya vienen filtrados y ordenados por el SP (Activos, Mostrar en Dashboard, Prioridad, Fecha)
                setAvisos(avisosTransformados);

            } catch (e) {
                console.error("Error cargando avisos:", e);
            } finally {
                setLoading(false);
            }
        };

        cargarAvisos();
    }, []);

    const handleFiltrosChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const avisosFiltrados = useMemo(() => {
        return avisos.filter(aviso => {
            const matchTexto = !filtros.texto ||
                (aviso.titulo || "").toLowerCase().includes(filtros.texto.toLowerCase()) ||
                (aviso.mensaje || "").toLowerCase().includes(filtros.texto.toLowerCase());

            const matchArea = filtros.area === 'Todas' || aviso.area === filtros.area;
            const matchNivel = filtros.nivel === 'Todas' || aviso.nivel === filtros.nivel;

            return matchTexto && matchArea && matchNivel;
        });
    }, [avisos, filtros]);

    const getNivelLabel = (nivel) => {
        switch (nivel) {
            case "alta": return "Alta";
            case "media": return "Media";
            default: return "Baja";
        }
    };

    const handleVerDetalle = (aviso) => {
        setAvisoSeleccionado(aviso);
        setShowModal(true);
    };

    return (
        <div className="intra-avisos-page">
            {/* HEADER */}
            <header className="intra-news-header">
                <div>
                    {onVolver && (
                        <button
                            type="button"
                            className="intra-button-back"
                            onClick={onVolver}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                marginBottom: '1rem',
                                backgroundColor: 'transparent',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                color: '#475569',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f1f5f9';
                                e.currentTarget.style.transform = 'translateX(-4px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.transform = 'translateX(0)';
                            }}
                        >
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>←</span>
                            <span>Volver al dashboard</span>
                        </button>
                    )}
                    <h1 className="intra-news-title">Avisos urgentes</h1>
                </div>
            </header>

            {/* FILTROS */}
            <section className="intra-news-toolbar">
                <div className="intra-news-filters" style={{ width: '100%', display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
                    <div className="intra-filter-group" style={{ flex: '1', minWidth: '200px' }}>
                        <label className="intra-label" htmlFor="filtroTexto">
                            Buscar
                        </label>
                        <input
                            id="filtroTexto"
                            name="texto"
                            type="text"
                            className="intra-input"
                            value={filtros.texto}
                            onChange={handleFiltrosChange}
                            placeholder="Buscar por título o mensaje..."
                        />
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroArea">
                            Área
                        </label>
                        <select
                            id="filtroArea"
                            name="area"
                            className="intra-select"
                            value={filtros.area}
                            onChange={handleFiltrosChange}
                        >
                            <option value="Todas">Todas</option>
                            {areasNegocio.map((area, idx) => (
                                <option key={area.idAreaNegocio || area.IdAreaNegocio || idx} value={area.nombre || area.Nombre || area.descripcion || area.Descripcion}>
                                    {area.nombre || area.Nombre || area.descripcion || area.Descripcion}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroNivel">
                            Prioridad
                        </label>
                        <select
                            id="filtroNivel"
                            name="nivel"
                            className="intra-select"
                            value={filtros.nivel}
                            onChange={handleFiltrosChange}
                        >
                            <option value="Todas">Todas</option>
                            <option value="alta">Alta</option>
                            <option value="media">Media</option>
                            <option value="baja">Baja</option>
                        </select>
                    </div>
                </div>
            </section>

            {/* CONTENIDO */}
            {loading ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
                    Cargando avisos...
                </p>
            ) : avisos.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem 2rem',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                }}>
                    <p style={{ fontSize: '1.125rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        No hay avisos urgentes activos
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                        Cuando haya avisos importantes, aparecer n aqu
                    </p>
                </div>
            ) : avisosFiltrados.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem 2rem',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                }}>
                    <p style={{ fontSize: '1.125rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        No se encontraron avisos
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                        Intenta ajustar los filtros de búsqueda.
                    </p>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1rem',
                }}>
                    {avisosFiltrados.map((aviso) => (
                        <article
                            key={aviso.id}
                            onClick={() => handleVerDetalle(aviso)}
                            style={{
                                backgroundColor: aviso.nivel === 'alta' ? '#fef2f2' : aviso.nivel === 'media' ? '#fffbeb' : '#f0f9ff',
                                border: `1px solid ${aviso.nivel === 'alta' ? '#ef4444' : aviso.nivel === 'media' ? '#f59e0b' : '#3b82f6'}`,
                                borderRadius: '16px',
                                overflow: 'hidden',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                cursor: 'pointer',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                                e.currentTarget.style.transform = 'translateY(-6px)';
                                e.currentTarget.style.borderColor = aviso.nivel === 'alta' ? '#b91c1c' : aviso.nivel === 'media' ? '#d97706' : '#2563eb';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = aviso.nivel === 'alta' ? '#ef4444' : aviso.nivel === 'media' ? '#f59e0b' : '#3b82f6';
                            }}
                        >
                            {/* Ribbon de urgencia */}
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '40px',
                                height: '40px',
                                zIndex: 10,
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: 0,
                                    height: 0,
                                    borderStyle: 'solid',
                                    borderWidth: '0 40px 40px 0',
                                    borderColor: `transparent ${aviso.nivel === 'alta' ? '#ef4444' : aviso.nivel === 'media' ? '#f59e0b' : '#3b82f6'} transparent transparent`,
                                }} />
                                <div style={{
                                    position: 'absolute',
                                    top: '3px',
                                    right: '5px',
                                    color: 'white',
                                    fontSize: '0.8rem',
                                    fontWeight: 'bold',
                                    zIndex: 11
                                }} title={`Prioridad: ${aviso.nivel}`}>
                                    {aviso.nivel === 'alta' ? 'A' : aviso.nivel === 'media' ? 'M' : 'B'}
                                </div>
                            </div>
                            
                            {/* Imagen */}
                            <div style={{
                                width: '100%',
                                height: '160px',
                                overflow: 'hidden',
                                backgroundColor: '#f8fafc',
                                position: 'relative'
                            }}>
                                <img
                                    src={getImageUrl(aviso.imagen) || PLACEHOLDER_IMAGE}
                                    alt={aviso.titulo}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                    onError={(e) => {
                                        if (e.target.src !== PLACEHOLDER_IMAGE) {
                                            e.target.src = PLACEHOLDER_IMAGE;
                                        }
                                    }}
                                />
                                {aviso.nivel === 'alta' && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '8px',
                                        left: '8px',
                                        backgroundColor: '#fef2f2',
                                        color: '#991b1b',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        fontSize: '0.65rem',
                                        fontWeight: '700',
                                        border: '1px solid #fecaca'
                                    }}>
                                        Urgente
                                    </div>
                                )}
                            </div>

                            {/* Contenido */}
                            <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {/* Area & Priority badges */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '0.75rem' }}>
                                    <span style={{
                                        backgroundColor: '#eff6ff',
                                        color: '#1e40af',
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        fontSize: '0.6rem',
                                        fontWeight: '700',
                                        textTransform: 'uppercase',
                                        border: '1px solid #bfdbfe'
                                    }}>
                                        {aviso.area || "Corporativo"}
                                    </span>
                                    <span style={{
                                        backgroundColor: aviso.nivel === 'alta' ? '#fef2f2' : aviso.nivel === 'media' ? '#fffbeb' : '#f0f9ff',
                                        color: aviso.nivel === 'alta' ? '#991b1b' : aviso.nivel === 'media' ? '#92400e' : '#1e40af',
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        fontSize: '0.6rem',
                                        fontWeight: '700',
                                        textTransform: 'uppercase',
                                        border: `1px solid ${aviso.nivel === 'alta' ? '#fecaca' : aviso.nivel === 'media' ? '#fde68a' : '#bfdbfe'}`
                                    }}>
                                        {getNivelLabel(aviso.nivel)}
                                    </span>
                                </div>

                                {/* Título */}
                                <h3 style={{
                                    fontSize: '0.95rem',
                                    fontWeight: '700',
                                    color: '#1e293b',
                                    margin: '0 0 0.5rem 0',
                                    lineHeight: '1.4',
                                    display: '-webkit-box',
                                    WebkitLineClamp: '2',
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    minHeight: '2.8em'
                                }}>
                                    {aviso.titulo}
                                </h3>

                                {/* Mensaje resumido */}
                                <p style={{
                                    fontSize: '0.8rem',
                                    color: '#64748b',
                                    marginBottom: '1rem',
                                    lineHeight: '1.5',
                                    display: '-webkit-box',
                                    WebkitLineClamp: '3',
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    flex: 1
                                }}>
                                    {aviso.mensaje}
                                </p>

                                {/* Footer (Vigencia) */}
                                <div style={{
                                    paddingTop: '0.75rem',
                                    marginTop: 'auto',
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    fontSize: '0.7rem',
                                    color: '#94a3b8'
                                }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        📅 {new Date(aviso.fechaInicio).toLocaleDateString()}
                                    </span>
                                    {aviso.fechaFin && (
                                        <span style={{ fontStyle: 'italic' }}>
                                            Hasta: {new Date(aviso.fechaFin).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {/* MODAL DE DETALLE */}
            {showModal && avisoSeleccionado && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '800px',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1e293b' }}>
                                Detalle del aviso
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '2rem',
                                    lineHeight: 1,
                                    color: '#64748b',
                                    cursor: 'pointer'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Modal Content - Scrollable */}
                        <div style={{ padding: '2rem', overflowY: 'auto' }}>
                            {/* Imagen */}
                            {avisoSeleccionado.imagen && (
                                <div style={{ marginBottom: '1.5rem', borderRadius: '8px', overflow: 'hidden' }}>
                                    <img
                                        src={getImageUrl(avisoSeleccionado.imagen) || PLACEHOLDER_IMAGE}
                                        alt={avisoSeleccionado.titulo}
                                        style={{
                                            width: '100%',
                                            maxHeight: '400px',
                                            objectFit: 'cover',
                                        }}
                                        onError={(e) => {
                                            if (e.target.src !== PLACEHOLDER_IMAGE) {
                                                e.target.src = PLACEHOLDER_IMAGE;
                                            }
                                        }}
                                    />
                                </div>
                            )}

                            {/* Meta info */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                <span
                                    className={`intra-badge-level intra-badge-level--${avisoSeleccionado.nivel}`}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '12px',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {getNivelLabel(avisoSeleccionado.nivel)} prioridad
                                </span>
                                {avisoSeleccionado.fechaInicio && (
                                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                        <strong>Publicado:</strong> {new Date(avisoSeleccionado.fechaInicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </span>
                                )}
                                {avisoSeleccionado.fechaFin && (
                                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                        <strong>Vigente hasta:</strong> {new Date(avisoSeleccionado.fechaFin).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </span>
                                )}
                            </div>

                            {/* T tulo */}
                            <h3 style={{
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                color: '#1e293b',
                                margin: '0 0 1rem 0',
                                lineHeight: 1.3
                            }}>
                                {avisoSeleccionado.titulo}
                            </h3>

                            {/* Mensaje completo */}
                            <div style={{
                                fontSize: '1rem',
                                color: '#475569',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap'
                            }}>
                                {avisoSeleccionado.mensaje}
                            </div>

                            {/* Área responsable */}
                            {avisoSeleccionado.area && (
                                <div style={{
                                    marginTop: '1.5rem',
                                    padding: '1rem',
                                    backgroundColor: '#f8fafc',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                                        <strong>Área responsable:</strong> {avisoSeleccionado.area}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AvisosUsuario;
